import { useCallback, useEffect, useMemo, useState } from "react";

import { BottomNav } from "@/components/cockpit/BottomNav";
import { DigestScorecard } from "@/components/cockpit/DigestScorecard";
import { Sidebar } from "@/components/cockpit/Sidebar";
import { VitalSignsRibbon, type VitalSigns } from "@/components/cockpit/VitalSignsRibbon";
import {
  type DigestEntry,
  type DigestResponse,
  type DigestSite,
  getDigest,
} from "@/lib/emails";
import { cn } from "@/lib/cn";

type DigestRole = "manager" | "founder";

interface State {
  data: DigestResponse | null;
  loading: boolean;
  error: string | null;
  role: DigestRole;
}

/** Management digest screen (brief §10, reference image 4).
 *
 *  Manager view: same scorecard grid filtered to the user's cluster
 *  (for v1 we don't have role-based cluster mapping, so manager and
 *  founder both see everything; the toggle is wired so the role
 *  semantic is in place for the multi-tenant deployment).
 *  Founder view: everything, network-wide totals at the top, with
 *  the escalations-needing-attention rail on the right. */
export function Digest() {
  const [state, setState] = useState<State>({
    data: null,
    loading: true,
    error: null,
    role: "manager",
  });
  const [signingOut, setSigningOut] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await getDigest();
      setState((prev) => ({ ...prev, data, loading: false, error: null }));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const networkTotals = useMemo(() => computeNetworkTotals(state.data?.sites ?? []), [
    state.data,
  ]);

  const vitals: VitalSigns = useMemo(
    () => ({
      sla_label: "live",
      queue_depth: networkTotals.total,
      due_today: 0,
      overdue: networkTotals.high,
      escalation_level:
        networkTotals.high > 0
          ? "critical"
          : networkTotals.medium > 0
            ? "active"
            : "ok",
    }),
    [networkTotals],
  );

  const escalations: DigestEntry[] = useMemo(() => {
    if (!state.data) return [];
    const rows: DigestEntry[] = [];
    for (const s of state.data.sites) rows.push(...s.entries);
    rows.sort((a, b) => severityRank(b.severity) - severityRank(a.severity));
    return rows.slice(0, 10);
  }, [state.data]);

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await fetch("/auth/logout", { method: "POST", credentials: "include" });
    } catch {
      /* still bounce */
    }
    window.location.href = "/login";
  }, []);

  return (
    <main className="flex min-h-screen bg-op-base text-op-primary">
      <Sidebar
        items={[
          { to: "/", label: "Cockpit", glyph: "◎" },
          { to: "/lanes", label: "Lanes", glyph: "▤" },
          { to: "/tasks", label: "Tasks", glyph: "☑" },
          { to: "/digest", label: "Digest", glyph: "△", badge: networkTotals.high },
          { to: "/dashboard", label: "Show everything", glyph: "⊞" },
        ]}
        onSignOut={handleSignOut}
        signingOut={signingOut}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <VitalSignsRibbon vitals={vitals} />

        <div className="flex-1 overflow-auto px-4 py-4 md:px-6">
          <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h1 className="font-serif text-2xl text-op-primary">
              Daily operations digest{" "}
              <span className="text-op-muted">— {todayLabel()}</span>
            </h1>
            <RoleSwitch
              role={state.role}
              onChange={(role) => setState((prev) => ({ ...prev, role }))}
            />
          </header>

          {state.error ? (
            <div
              role="alert"
              data-testid="digest-error"
              className="mb-3 rounded border border-op-border bg-op-elevated px-3 py-2 text-xs text-op-secondary"
            >
              {state.error}
            </div>
          ) : null}

          <NetworkTotals totals={networkTotals} />

          <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_320px]">
            <SiteGrid sites={state.data?.sites ?? []} loading={state.loading} />
            <EscalationsRail entries={escalations} />
          </div>
        </div>

        <BottomNav
          items={[
            { to: "/", label: "Cockpit", glyph: "◎" },
            { to: "/lanes", label: "Lanes", glyph: "▤" },
            { to: "/tasks", label: "Tasks", glyph: "☑" },
            { to: "/digest", label: "Esc.", glyph: "△", badge: networkTotals.high },
          ]}
        />
      </div>
    </main>
  );
}

function todayLabel(): string {
  return new Date().toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
  });
}

interface NetworkTotalsValue {
  total: number;
  high: number;
  medium: number;
  low: number;
  sites: number;
}

function computeNetworkTotals(sites: DigestSite[]): NetworkTotalsValue {
  let high = 0;
  let medium = 0;
  let low = 0;
  let total = 0;
  for (const s of sites) {
    high += s.severity_counts.high;
    medium += s.severity_counts.medium;
    low += s.severity_counts.low;
    total += s.entries.length;
  }
  return { high, medium, low, total, sites: sites.length };
}

function NetworkTotals({ totals }: { totals: NetworkTotalsValue }) {
  return (
    <section
      data-testid="digest-network-totals"
      className="grid gap-3 sm:grid-cols-3"
    >
      <TotalCard
        label="Network open cases"
        value={totals.total}
        accent="text-op-primary"
      />
      <TotalCard
        label="Overdue / high severity"
        value={totals.high}
        accent={
          totals.high > 0 ? "text-bucket-need-reply-now" : "text-op-secondary"
        }
      />
      <TotalCard
        label="Escalations"
        value={totals.medium + totals.high}
        accent={
          totals.high + totals.medium > 0 ? "text-op-amber" : "text-op-secondary"
        }
      />
    </section>
  );
}

function TotalCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent: string;
}) {
  return (
    <article className="rounded border border-op-border bg-op-surface px-4 py-3">
      <p className="text-[10px] uppercase tracking-wider text-op-muted">
        {label}
      </p>
      <p className={cn("mt-1 font-mono text-2xl", accent)}>{value}</p>
    </article>
  );
}

function SiteGrid({
  sites,
  loading,
}: {
  sites: DigestSite[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <p
        data-testid="digest-loading"
        className="rounded border border-dashed border-op-border px-4 py-10 text-center text-sm text-op-muted"
      >
        Loading digest…
      </p>
    );
  }
  if (sites.length === 0) {
    return (
      <p
        data-testid="digest-empty"
        className="rounded border border-dashed border-op-border px-4 py-10 text-center text-sm text-op-muted"
      >
        No escalations in the last 7 days. The cockpit and lane board are caught up.
      </p>
    );
  }
  return (
    <section
      data-testid="digest-scorecards"
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
    >
      {sites.map((site) => (
        <DigestScorecard key={site.site} site={site} />
      ))}
    </section>
  );
}

function EscalationsRail({ entries }: { entries: DigestEntry[] }) {
  return (
    <aside
      data-testid="digest-rail"
      className="rounded border border-op-border bg-op-surface px-3 py-3"
    >
      <h2 className="mb-3 text-[10px] uppercase tracking-wider text-op-muted">
        Escalations needing attention
      </h2>
      {entries.length === 0 ? (
        <p className="text-xs text-op-muted">Nothing pending.</p>
      ) : (
        <ol className="space-y-2">
          {entries.map((e) => (
            <li
              key={e.id}
              data-testid="digest-rail-entry"
              data-severity={e.severity}
              className="rounded border border-op-border bg-op-base px-3 py-2"
            >
              <p className="text-xs font-medium text-op-primary">
                {e.client || e.site}
              </p>
              <p className="mt-1 text-[11px] text-op-secondary">{e.headline}</p>
              {e.recommended_action ? (
                <p className="mt-1 text-[11px] text-op-muted">
                  {e.recommended_action}
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}

function RoleSwitch({
  role,
  onChange,
}: {
  role: DigestRole;
  onChange: (role: DigestRole) => void;
}) {
  return (
    <div
      data-testid="digest-role-switch"
      role="tablist"
      className="inline-flex rounded border border-op-border bg-op-elevated p-1 text-xs"
    >
      {(["manager", "founder"] as DigestRole[]).map((r) => (
        <button
          key={r}
          type="button"
          role="tab"
          data-testid={`digest-role-${r}`}
          aria-selected={role === r}
          onClick={() => onChange(r)}
          className={cn(
            "rounded px-3 py-1 capitalize transition-colors",
            role === r
              ? "bg-op-accent-muted text-op-primary"
              : "text-op-secondary hover:bg-op-base hover:text-op-primary",
          )}
        >
          {r}
        </button>
      ))}
    </div>
  );
}

function severityRank(s: string): number {
  if (s === "high") return 3;
  if (s === "medium") return 2;
  return 1;
}
